import baseApi from "./RootBaseApi";
import {
  SyntheticBookingCanaryConfig,
  UpdateSyntheticBookingCanaryConfigPayload,
  CanaryStatus,
  CanaryRun,
  CanaryRunsQueryParams,
  CanaryRunsResponse,
  TriggerCanaryRunResponse,
  CancelCanaryRunResponse,
} from "@/types/booking-canary.types";

export const BookingCanaryService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSyntheticBookingCanaryConfig: builder.query<
      SyntheticBookingCanaryConfig,
      void
    >({
      query: () => ({
        url: "system-config/synthetic-booking-canary",
        method: "GET",
      }),
      // This endpoint wraps its payload in { status, message, data }, like
      // other system-config endpoints (see PlatformSettingsService.ts).
      transformResponse: (response: { data: SyntheticBookingCanaryConfig }) =>
        response.data,
      providesTags: ["SyntheticBookingCanaryConfig"],
    }),

    updateSyntheticBookingCanaryConfig: builder.mutation<
      SyntheticBookingCanaryConfig,
      UpdateSyntheticBookingCanaryConfigPayload
    >({
      query: (data) => ({
        url: "system-config/synthetic-booking-canary",
        method: "PUT",
        data,
      }),
      transformResponse: (response: { data: SyntheticBookingCanaryConfig }) =>
        response.data,
      invalidatesTags: ["SyntheticBookingCanaryConfig"],
    }),

    getCanaryStatus: builder.query<CanaryStatus, void>({
      query: () => ({
        url: "booking-canary/admin/status",
        method: "GET",
      }),
      providesTags: ["CanaryStatus"],
    }),

    getCanaryRuns: builder.query<CanaryRunsResponse, CanaryRunsQueryParams>({
      query: (params) => ({
        url: "booking-canary/admin/runs",
        method: "GET",
        params,
      }),
      // Backend returns { items, total, page, limit, totalPages } (matching
      // its accounting-export pagination precedent) rather than this repo's
      // usual { data, total_items, current_page, total_pages } envelope
      // (ActivityLogResponse) — adapt here so the rest of the app can keep
      // using the one shape consistently.
      transformResponse: (response: {
        items: CanaryRun[];
        total: number;
        page: number;
        totalPages: number;
      }): CanaryRunsResponse => ({
        data: response.items,
        total_items: response.total,
        current_page: response.page,
        total_pages: response.totalPages,
      }),
      providesTags: ["CanaryRuns"],
    }),

    triggerCanaryRun: builder.mutation<TriggerCanaryRunResponse, void>({
      query: () => ({
        url: "booking-canary/admin/run",
        method: "POST",
      }),
      invalidatesTags: ["CanaryStatus", "CanaryRuns"],
    }),

    cancelCanaryRun: builder.mutation<CancelCanaryRunResponse, void>({
      query: () => ({
        url: "booking-canary/admin/cancel",
        method: "POST",
      }),
      invalidatesTags: ["CanaryStatus", "CanaryRuns"],
    }),
  }),
});

export const {
  useGetSyntheticBookingCanaryConfigQuery,
  useUpdateSyntheticBookingCanaryConfigMutation,
  useGetCanaryStatusQuery,
  useGetCanaryRunsQuery,
  useTriggerCanaryRunMutation,
  useCancelCanaryRunMutation,
} = BookingCanaryService;
